/**
 * Iter123 — Case Closure Wizard.
 *
 * Multi-step closure flow: outcome + narrative + acknowledgments + e-sign.
 * Backend: POST /api/cases/{id}/close — sets status=CLOSED, freezes edits.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import ESignaturePanel from './ESignaturePanel';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { Archive, ChevronRight, ChevronLeft, Loader2, CheckCircle2 } from 'lucide-react';

const OUTCOMES = [
  { value: 'RESOLVED', label: 'Resolved — objectives met' },
  { value: 'CLIENT_REQUEST', label: 'Closed at client request' },
  { value: 'UNSUCCESSFUL', label: 'Unsuccessful — no further leads' },
  { value: 'REFERRED', label: 'Referred to another agency' },
  { value: 'ADMINISTRATIVE', label: 'Administrative closure' },
];

export default function CaseClosureWizard({ open, caseData, onClose, onClosed }) {
  const token = localStorage.getItem('nexus_token');
  const [step, setStep] = useState(1);
  const [outcome, setOutcome] = useState('RESOLVED');
  const [summary, setSummary] = useState('');
  const [finalCost, setFinalCost] = useState('');
  const [acks, setAcks] = useState({});
  const [signed, setSigned] = useState(null);
  const [busy, setBusy] = useState(false);

  const acksReady = ['freeze', 'archive', 'evidence'].every(k => acks[k]);
  const step1Ready = outcome && summary.trim().length >= 20;

  const closeCase = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/cases/${caseData.id}/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          outcome, summary, final_cost: finalCost ? parseFloat(finalCost) : null,
          signature_id: signed?.id,
        }),
      });
      const d = await r.json();
      if (!r.ok) return toast({ title: 'Close failed', description: d.detail || 'Error', variant: 'destructive' });
      toast({ title: 'Case closed', description: 'The case is now read-only and archived.' });
      if (onClosed) onClosed(d);
      onClose();
    } finally { setBusy(false); }
  };

  const reset = () => { setStep(1); setOutcome('RESOLVED'); setSummary(''); setFinalCost(''); setAcks({}); setSigned(null); };
  const handleClose = () => { reset(); onClose(); };

  return (
    <Dialog open={open} onOpenChange={o => !o && handleClose()}>
      <DialogContent className="bg-[#131A2A] border-slate-700 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="w-5 h-5 text-amber-400" /> Close Case
            <span className="text-xs text-slate-400 font-mono ml-2">Step {step}/3</span>
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            <p className="text-xs text-slate-400">Provide closure outcome and a final narrative. Once closed, the case becomes read-only and moves to the Closed tab.</p>
            <div>
              <label className="text-xs text-slate-400">Outcome</label>
              <Select value={outcome} onValueChange={setOutcome}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="closure-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>{OUTCOMES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400">Final Cost (optional)</label>
              <Input type="number" step="0.01" value={finalCost} onChange={e => setFinalCost(e.target.value)}
                placeholder="0.00" className="bg-slate-800 border-slate-600 text-white" data-testid="closure-cost" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Closure Narrative *</label>
              <Textarea value={summary} onChange={e => setSummary(e.target.value)}
                rows={5} placeholder="Summarize findings, actions taken, and reason for closure (min 20 characters)…"
                className="bg-slate-800 border-slate-600 text-white" data-testid="closure-summary" />
              <div className="text-[10px] text-slate-500 mt-1">{summary.length} characters</div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <p className="text-xs text-slate-400">Confirm the closure acknowledgments below.</p>
            {[
              { key: 'freeze', text: 'I understand that after closing, no further edits can be made to this case without an admin reopen.' },
              { key: 'archive', text: 'The case will move to the Closed tab and remain fully searchable.' },
              { key: 'evidence', text: 'All evidence, reports, and time entries have been reviewed and finalized.' },
            ].map(a => (
              <label key={a.key} className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox checked={!!acks[a.key]} onCheckedChange={v => setAcks(prev => ({ ...prev, [a.key]: !!v }))} data-testid={`closure-ack-${a.key}`} />
                <span className="text-slate-200">{a.text}</span>
              </label>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <p className="text-xs text-slate-400">Sign the closure with your typed name. IP + timestamp will be recorded.</p>
            <ESignaturePanel
              documentType="case_acceptance"
              documentId={`CLOSE-${caseData?.id}`}
              caseId={caseData?.id}
              acknowledgments={[
                { key: 'authority', label: 'I have the authority to close this case.' },
                { key: 'accuracy', label: 'The closure narrative is accurate.' },
              ]}
              compact
              onSigned={sig => setSigned(sig)}
            />
            {signed && (
              <div className="text-xs text-emerald-300 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Signed by {signed.typed_name}</div>
            )}
          </div>
        )}

        <div className="flex justify-between pt-3 border-t border-slate-700">
          {step > 1 ? (
            <Button variant="outline" className="border-slate-500 !text-slate-200" onClick={() => setStep(step - 1)}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          ) : <div />}
          {step < 3 && (
            <Button className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold"
              disabled={(step === 1 && !step1Ready) || (step === 2 && !acksReady)}
              onClick={() => setStep(step + 1)} data-testid="closure-next-btn">
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
          {step === 3 && (
            <Button className="bg-red-600 hover:bg-red-500 text-white" onClick={closeCase} disabled={busy || !signed} data-testid="closure-finish-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Archive className="w-4 h-4 mr-1" />} Close Case
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
